import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ChevronLeft, ChevronRight, ChevronDown, Building2, Flame, Trash2, Bell, BellOff, UserCheck, TrendingUp, Clock } from 'lucide-react';
import { useFilterParams } from "@/store/filtersStore";
import { useUserStore } from '@/store/userStore';
import {
  fetchNearMisses, fetchNMResumen, fetchNMPorCategoria, fetchNMTimeline, fetchNMPorGravedad,
  fetchFilterGravedades, fetchFilterEstados, fetchFilterClasificaciones, fetchFilterNMCentros,
  fetchFilterNMResponsables, fetchNMResumenResponsables,
} from '@/services/api';
import { KpiGlowCard } from '@/components/dashboard/KpiGlowCard';
import { ChartCard } from '@/components/dashboard/ChartCard';
import { DonutChart, TrendLineChart, HorizontalBarChart } from '@/components/dashboard/Charts';
import { GlobalFilterBar } from '@/components/dashboard/GlobalFilterBar';
import { ModuleFilterBar } from '@/components/common/ModuleFilterBar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import ExportButtons from '@/components/common/ExportButtons';
import { PhotoViewerButton } from '@/components/common/PhotoViewer';
import { TrazabilidadFooter } from '@/components/common/TrazabilidadFooter';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const _DIAS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const getDia = (fecha) => { try { return _DIAS[new Date(fecha + 'T12:00:00').getDay()]; } catch { return '-'; } };

// Severity colors tuned for accessible contrast on BOTH light and dark themes.
// Amarillo was originally #FFD600 — too washed out on white backgrounds.
// Now using a deeper amber (#D4A300) that maintains AA contrast in both modes.
const GRAVEDAD_COLORS = {
  Verde: '#00A847', verde: '#00A847',
  Amarillo: '#D4A300', amarillo: '#D4A300',
  Naranja: '#FF6B35', naranja: '#FF6B35',
  Rojo: '#E53935', rojo: '#E53935',
};

const CATEGORIA_NM_LABELS = {
  caidas_mismo_distinto: 'Caídas a mismo/distinto nivel',
  cinturon_seguridad: 'Cinturón de seguridad',
  elementos_auxiliares: 'Elementos auxiliares',
  emergencias: 'Emergencias',
  herramientas_manuales: 'Herramientas manuales',
  maquinaria_fija: 'Maquinaria fija',
  maquinaria_movil: 'Maquinaria móvil',
  orden_limpieza: 'Orden y limpieza',
  otros: 'Otros',
  proceso_trabajo: 'Proceso de trabajo',
  senalizacion: 'Señalización',
  uso_epis: 'Uso EPIs',
};
const categoriaNmLabel = (raw) => CATEGORIA_NM_LABELS[(raw || '').trim().toLowerCase()] || (raw ? raw.replace(/_/g, ' ') : '-');

const NM_FILTER_DEFS = [
  { key: 'desde',        label: 'Desde',          type: 'date' },
  { key: 'hasta',        label: 'Hasta',          type: 'date' },
  { key: 'sede',         label: 'Sede / Centro',  type: 'select', fetchFn: fetchFilterNMCentros },
  { key: 'responsable',  label: 'Responsable',    type: 'select', fetchFn: fetchFilterNMResponsables },
  { key: 'gravedad',     label: 'Gravedad',       type: 'select', fetchFn: fetchFilterGravedades },
  { key: 'estado',       label: 'Estado',         type: 'select', fetchFn: fetchFilterEstados },
  { key: 'categoria',    label: 'Categoría NM',   type: 'select', fetchFn: fetchFilterClasificaciones },
];

const FIELD_LABELS_NM = {
  fecha: 'Fecha', centro_nombre: 'Sede', centro_codigo: 'Código de sede', clasificacion_nm: 'Clasificación',
  reportado_persona: 'Reportado por', reportado_otro_nombre: 'Nombre completo (Otro)', reportado_email: 'Email reportador', reportado_pnr: 'PNR reportador',
  responsable_nombre: 'Responsable', responsable_rol: 'Rol del responsable', responsable_email: 'Email responsable', responsable_pnr: 'PNR responsable',
  categoria_nm: 'Categoría', zona_lugar: 'Zona/Lugar', zona_lugar_otro: 'Zona/Lugar (detalle)',
  gravedad: 'Gravedad',
  estado: 'Estado',
  accion_tomada_tipo: 'Acción tomada', accion_tomada_otro: 'Descripción de la acción',
  fecha_cierre: 'Fecha de cierre',
  descripcion: 'Descripción', descripcion_nm: 'Descripción NM',
  sugerencia_subsanacion: 'Sugerencia de subsanación', opinion_tecnico: 'Opinión del técnico',
  accion_inmediata: 'Acción inmediata',
  medidas_correctivas: 'Medidas correctivas',
  tipo_actividad: 'Tipo de actividad', tipo_incidente: 'Tipo de incidente',
  fecharegistro: 'Fecha de registro',
};
const SKIP_FIELDS_NM = new Set(['id', '_id', 'objectid', 'globalid']);

const formatFieldValueNM = (value) => {
  if (typeof value === 'number' && value > 1_000_000_000_000) {
    return new Date(value).toLocaleString('es-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  }
  return String(value).replace(/_/g, ' ');
};

const LONG_TEXT_FIELDS_NM = new Set(['descripcion', 'accion_inmediata', 'observacion', 'sugerencia_subsanacion', 'opinion_tecnico', 'accion_tomada_otro']);

const UPPERCASE_FIELDS_NM = new Set(['reportado_persona', 'responsable_nombre', 'reportado_otro_nombre']);

// Fields that belong to the highlighted "Seguimiento" section
const SEGUIMIENTO_FIELDS = new Set(['estado', 'accion_tomada_tipo', 'accion_tomada_otro', 'fecha_cierre']);
// Fields grouped under "Detectado por"
const REPORTADO_FIELDS = new Set(['reportado_persona', 'reportado_otro_nombre', 'reportado_email', 'reportado_pnr']);
// Fields grouped under "Responsable"
const RESPONSABLE_FIELDS = new Set(['responsable_nombre', 'responsable_rol', 'responsable_email', 'responsable_pnr']);

const ROL_LABELS = {
  gerente: 'Gerente', jefe: 'Jefe de centro', mando_intermedio: 'Mando intermedio', otro: 'Otro',
};
const ACCION_LABELS = {
  igual: 'Igual a la sugerida', otra: 'Otra acción',
};

function SeguimientoSection({ row }) {
  const estadoCerrado = (row.estado || '').toLowerCase() === 'cerrado';
  const hasData = SEGUIMIENTO_FIELDS.size > 0 &&
    [...SEGUIMIENTO_FIELDS].some(k => row[k] != null && row[k] !== '' && row[k] !== '-');
  if (!hasData) return null;

  const estadoColor = estadoCerrado ? '#00A847' : '#FF6B35';
  const estadoBg = estadoCerrado ? 'rgba(0,168,71,0.08)' : 'rgba(255,107,53,0.08)';
  const estadoBorder = estadoCerrado ? 'rgba(0,168,71,0.25)' : 'rgba(255,107,53,0.25)';

  return (
    <div className="px-4 py-3 border-t" style={{ borderColor: 'var(--vz-border-subtle)', background: estadoBg }}>
      <p className="text-[9px] uppercase tracking-wider font-bold mb-2" style={{ color: estadoColor }}>
        Seguimiento del Near Miss
      </p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2">
        {row.estado && (
          <div>
            <p className="text-[9px] uppercase tracking-wider font-semibold mb-0.5" style={{ color: 'var(--vz-text-muted)' }}>Estado</p>
            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold"
              style={{ background: `${estadoColor}20`, color: estadoColor, border: `1px solid ${estadoBorder}` }}>
              {row.estado}
            </span>
          </div>
        )}
        {row.fecha_cierre && (
          <div>
            <p className="text-[9px] uppercase tracking-wider font-semibold mb-0.5" style={{ color: 'var(--vz-text-muted)' }}>Fecha de cierre</p>
            <p className="text-xs" style={{ color: 'var(--vz-text-secondary)' }}>{row.fecha_cierre}</p>
          </div>
        )}
        {row.accion_tomada_tipo && (
          <div>
            <p className="text-[9px] uppercase tracking-wider font-semibold mb-0.5" style={{ color: 'var(--vz-text-muted)' }}>Acción tomada</p>
            <p className="text-xs" style={{ color: 'var(--vz-text-secondary)' }}>
              {ACCION_LABELS[row.accion_tomada_tipo] || row.accion_tomada_tipo.replace(/_/g, ' ')}
            </p>
          </div>
        )}
      </div>
      {row.accion_tomada_otro && (
        <div className="mt-2">
          <p className="text-[9px] uppercase tracking-wider font-semibold mb-0.5" style={{ color: 'var(--vz-text-muted)' }}>Descripción de la acción</p>
          <p className="text-xs leading-relaxed whitespace-pre-wrap" style={{ color: 'var(--vz-text-secondary)' }}>{row.accion_tomada_otro}</p>
        </div>
      )}
    </div>
  );
}

function PersonasSection({ row }) {
  const hasReportado = [...REPORTADO_FIELDS].some(k => row[k] != null && row[k] !== '');
  const hasResponsable = [...RESPONSABLE_FIELDS].some(k => row[k] != null && row[k] !== '');
  if (!hasReportado && !hasResponsable) return null;

  return (
    <div className="px-4 py-3 border-t grid grid-cols-1 sm:grid-cols-2 gap-4" style={{ borderColor: 'var(--vz-border-subtle)' }}>
      {hasReportado && (
        <div>
          <p className="text-[9px] uppercase tracking-wider font-bold mb-2" style={{ color: '#4A9EFF' }}>Detectado por</p>
          <div className="space-y-1.5">
            {row.reportado_persona && (
              <div>
                <p className="text-[9px] uppercase tracking-wider font-semibold" style={{ color: 'var(--vz-text-muted)' }}>Nombre</p>
                <p className="text-xs uppercase font-medium" style={{ color: 'var(--vz-text-secondary)' }}>{prettifyPersonName(row.reportado_persona)}</p>
              </div>
            )}
            {row.reportado_otro_nombre && (
              <div>
                <p className="text-[9px] uppercase tracking-wider font-semibold" style={{ color: 'var(--vz-text-muted)' }}>Nombre completo</p>
                <p className="text-xs" style={{ color: 'var(--vz-text-secondary)' }}>{row.reportado_otro_nombre}</p>
              </div>
            )}
            {row.reportado_email && (
              <div>
                <p className="text-[9px] uppercase tracking-wider font-semibold" style={{ color: 'var(--vz-text-muted)' }}>Email</p>
                <p className="text-xs font-mono" style={{ color: 'var(--vz-text-muted)' }}>{row.reportado_email}</p>
              </div>
            )}
            {row.reportado_pnr && (
              <div>
                <p className="text-[9px] uppercase tracking-wider font-semibold" style={{ color: 'var(--vz-text-muted)' }}>PNR</p>
                <p className="text-xs font-mono" style={{ color: 'var(--vz-text-secondary)' }}>{row.reportado_pnr}</p>
              </div>
            )}
          </div>
        </div>
      )}
      {hasResponsable && (
        <div>
          <p className="text-[9px] uppercase tracking-wider font-bold mb-2" style={{ color: '#FF9800' }}>Responsable</p>
          <div className="space-y-1.5">
            {row.responsable_nombre && (
              <div>
                <p className="text-[9px] uppercase tracking-wider font-semibold" style={{ color: 'var(--vz-text-muted)' }}>Nombre</p>
                <p className="text-xs uppercase font-medium" style={{ color: 'var(--vz-text-secondary)' }}>{prettifyPersonName(row.responsable_nombre)}</p>
              </div>
            )}
            {row.responsable_rol && (
              <div>
                <p className="text-[9px] uppercase tracking-wider font-semibold" style={{ color: 'var(--vz-text-muted)' }}>Rol</p>
                <p className="text-xs" style={{ color: 'var(--vz-text-secondary)' }}>
                  {ROL_LABELS[row.responsable_rol] || row.responsable_rol.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                </p>
              </div>
            )}
            {row.responsable_email && (
              <div>
                <p className="text-[9px] uppercase tracking-wider font-semibold" style={{ color: 'var(--vz-text-muted)' }}>Email</p>
                <p className="text-xs font-mono" style={{ color: 'var(--vz-text-muted)' }}>{row.responsable_email}</p>
              </div>
            )}
            {row.responsable_pnr && (
              <div>
                <p className="text-[9px] uppercase tracking-wider font-semibold" style={{ color: 'var(--vz-text-muted)' }}>PNR</p>
                <p className="text-xs font-mono" style={{ color: 'var(--vz-text-secondary)' }}>{row.responsable_pnr}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Visual remap of multi-person team names that come glued together from ArcGIS
// dropdowns (e.g. `jordi_lahora_ivan_edo` saved as a single option). Display only —
// the underlying record value is preserved for filters.
const NAME_VISUAL_REMAP = [
  // Combined entry from ArcGIS survey — separator can be underscore, space or hyphen
  { match: /jordi[\s_]*lahora[\s_/-]*iv[aá]n[\s_]*edo/i, display: 'Jordi Lahora / Iván Edo' },
];

const prettifyPersonName = (raw) => {
  if (raw == null) return raw;
  const s = String(raw).trim();
  for (const rule of NAME_VISUAL_REMAP) {
    if (rule.match.test(s)) return rule.display;
  }
  // Replace underscores with spaces, then Title Case
  return s.replace(/_/g, ' ').replace(/\w\S*/g, w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
};

function ExpandedDetailNM({ row }) {
  const gravColor = (g) => GRAVEDAD_COLORS[g] || GRAVEDAD_COLORS[g?.toLowerCase()] || '#546E7A';
  // Exclude fields shown in the dedicated sections (personas + seguimiento)
  const DEDICATED_FIELDS = new Set([...SEGUIMIENTO_FIELDS, ...REPORTADO_FIELDS, ...RESPONSABLE_FIELDS]);
  const entries = Object.entries(row).filter(
    ([k, v]) => !SKIP_FIELDS_NM.has(k.toLowerCase()) && !DEDICATED_FIELDS.has(k) && v != null && v !== '' && v !== '-'
  );
  const longEntries = entries.filter(([k]) => LONG_TEXT_FIELDS_NM.has(k));
  const shortEntries = entries.filter(([k]) => !LONG_TEXT_FIELDS_NM.has(k));
  return (
    <div style={{ background: 'var(--vz-bg-elevated)' }}>
      {/* Seguimiento section (highlighted) */}
      <SeguimientoSection row={row} />
      {/* Personas: Detectado por + Responsable */}
      <PersonasSection row={row} />
      {/* General fields grid */}
      {shortEntries.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-3 p-4">
          {shortEntries.map(([key, value]) => (
            <div key={key} className="min-w-0">
              <p className="text-[9px] uppercase tracking-wider font-semibold mb-0.5" style={{ color: 'var(--vz-text-muted)' }}>
                {FIELD_LABELS_NM[key] || key.replace(/_/g, ' ')}
              </p>
              <p
                className={`text-xs truncate ${UPPERCASE_FIELDS_NM.has(key) ? 'uppercase' : ''}`}
                style={{ color: key === 'gravedad' ? gravColor(value) : 'var(--vz-text-secondary)' }}
                title={formatFieldValueNM(value)}
              >
                {key === 'gravedad'
                  ? String(value).replace(/_/g, ' ')
                  : (UPPERCASE_FIELDS_NM.has(key) ? prettifyPersonName(value) : formatFieldValueNM(value))}
              </p>
            </div>
          ))}
        </div>
      )}
      {/* Long text fields */}
      {longEntries.length > 0 && (
        <div className="px-4 pb-4 space-y-3 border-t" style={{ borderColor: 'var(--vz-border-subtle)' }}>
          {longEntries.map(([key, value]) => (
            <div key={key}>
              <p className="text-[9px] uppercase tracking-wider font-semibold mb-1" style={{ color: 'var(--vz-text-muted)' }}>
                {FIELD_LABELS_NM[key] || key.replace(/_/g, ' ')}
              </p>
              <p className="text-xs leading-relaxed whitespace-pre-wrap" style={{ color: 'var(--vz-text-secondary)' }}>
                {String(value)}
              </p>
            </div>
          ))}
        </div>
      )}
      {(row.objectid || row.OBJECTID) && (
        <div className="px-4 pb-3 flex items-center gap-2">
          <PhotoViewerButton module="near-misses" objectid={row.objectid || row.OBJECTID} />
        </div>
      )}
      <TrazabilidadFooter row={row} />
    </div>
  );
}

function NMTable({ filters = {} }) {
  const [page, setPage] = useState(1);
  const [expandedRow, setExpandedRow] = useState(null);
  const globalFilters = useFilterParams();
  const { token, user } = useUserStore();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const queryClient = useQueryClient();
  const baseFilters = { desde: globalFilters.desde, hasta: globalFilters.hasta };
  const combinedFilters = { ...baseFilters, ...filters, page, limit: 50 };

  // Reset to page 1 when filters change
  const filtersKey = JSON.stringify(filters);
  React.useEffect(() => { setPage(1); setExpandedRow(null); }, [filtersKey]);

  const { data: listData, isLoading } = useQuery({
    queryKey: ['nm-list', combinedFilters],
    queryFn: () => fetchNearMisses(combinedFilters),
    enabled: !!token,
  });

  const deleteRecord = async (objectid, e) => {
    e.stopPropagation();
    if (!window.confirm('¿Eliminar este Near Miss? Esta acción no se puede deshacer.')) return;
    try {
      const tok = localStorage.getItem('valoriza_token');
      await axios.delete(`${API}/api/near-misses/${objectid}`, { headers: { Authorization: `Bearer ${tok}` } });
      toast.success('Registro eliminado');
      queryClient.invalidateQueries({ queryKey: ['nm-list'] });
    } catch { toast.error('Error al eliminar'); }
  };

  const rows  = listData?.data  || [];
  const total = listData?.total || 0;
  const totalPages = Math.ceil(total / 50);

  const COLS = ['', 'Día', 'Fecha', 'Sede', 'Clasificación', 'Reportado por', 'Responsable', 'Categoría', 'Zona/Lugar', 'Gravedad', 'Estado', ''];

  const gravColor = (g) => GRAVEDAD_COLORS[g] || GRAVEDAD_COLORS[g?.toLowerCase()] || '#546E7A';
  const toggleRow = (i) => setExpandedRow(prev => prev === i ? null : i);

  return (
    <div data-testid="nm-table-container">
      <div className="flex items-center justify-between px-1 mb-2">
        <span className="text-xs" style={{ color: 'var(--vz-text-muted)' }}>{total.toLocaleString('es-ES')} registros</span>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="h-7 w-7" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft size={14} /></Button>
          <span className="text-xs" style={{ color: 'var(--vz-text-secondary)' }}>{page} / {totalPages || 1}</span>
          <Button variant="ghost" size="icon" className="h-7 w-7" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight size={14} /></Button>
        </div>
      </div>
      <ScrollArea className="h-[420px]">
        <Table>
          <TableHeader>
            <TableRow style={{ borderColor: 'var(--vz-border-subtle)' }}>
              {COLS.map((h, idx) => (
                <TableHead key={idx} className="text-[10px] uppercase tracking-wider font-montserrat sticky top-0" style={{ color: 'var(--vz-text-muted)', background: 'var(--vz-bg-card)', width: idx === 0 ? 28 : undefined }}>{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading
              ? Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i} style={{ borderColor: 'var(--vz-border-subtle)' }}>
                    {COLS.map((_, j) => <TableCell key={j}><div className="h-3 rounded animate-pulse" style={{ background: 'var(--vz-bg-elevated)', width: '80%' }} /></TableCell>)}
                  </TableRow>
                ))
              : rows.length === 0
                ? <TableRow><TableCell colSpan={12} className="text-center text-xs py-8" style={{ color: 'var(--vz-text-muted)' }}>Sin registros para este filtro</TableCell></TableRow>
                : rows.map((row, i) => (
                    <React.Fragment key={i}>
                      <TableRow
                        onClick={() => toggleRow(i)}
                        className="hover:bg-[#132860] transition-colors duration-200 cursor-pointer"
                        style={{ borderColor: 'var(--vz-border-subtle)', background: expandedRow === i ? 'rgba(255,107,53,0.05)' : undefined }}
                        data-testid="nm-table-row"
                      >
                        <TableCell className="pl-3 pr-1 w-7">
                          <ChevronDown size={12} className="transition-transform duration-200" style={{ color: 'var(--vz-text-muted)', transform: expandedRow === i ? 'rotate(0deg)' : 'rotate(-90deg)' }} />
                        </TableCell>
                        <TableCell className="text-[10px] font-medium w-8 text-center" style={{ color: 'var(--vz-text-muted)' }}>{getDia(row.fecha)}</TableCell>
                        <TableCell className="text-xs font-mono-custom whitespace-nowrap" style={{ color: 'var(--vz-text-muted)' }}>{row.fecha || '-'}</TableCell>
                        <TableCell className="text-xs max-w-[150px]" style={{ color: 'var(--vz-text-primary)' }} title={(row.centro_nombre || '') + (row.centro_codigo ? ` · ${row.centro_codigo}` : '')}>
                          <div className="leading-tight font-medium">
                            <div className="truncate">{row.centro_nombre || '-'}</div>
                            {row.centro_codigo && <div className="text-[10px] font-mono opacity-50">{row.centro_codigo}</div>}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs max-w-[110px] truncate" style={{ color: 'var(--vz-text-secondary)' }}>{(row.clasificacion_nm?.replace(/_/g, ' ') || 'Leve')}</TableCell>
                        <TableCell className="text-xs max-w-[120px] truncate uppercase" style={{ color: 'var(--vz-text-secondary)' }}>{prettifyPersonName(row.reportado_persona) || '-'}</TableCell>
                        <TableCell className="text-xs max-w-[120px] truncate uppercase" style={{ color: 'var(--vz-text-muted)' }}>{prettifyPersonName(row.responsable_nombre) || '-'}</TableCell>
                        <TableCell className="text-xs max-w-[120px] truncate" style={{ color: 'var(--vz-text-secondary)' }}>{categoriaNmLabel(row.categoria_nm)}</TableCell>
                        <TableCell className="text-xs max-w-[110px] truncate" style={{ color: 'var(--vz-text-muted)' }}>{row.zona_lugar?.replace(/_/g, ' ') || '-'}</TableCell>
                        <TableCell>
                          <Badge className="text-[10px] capitalize font-semibold" style={{
                            background: `${gravColor(row.gravedad)}28`,
                            color: gravColor(row.gravedad),
                            border: `1px solid ${gravColor(row.gravedad)}80`,
                          }}>{row.gravedad || '-'}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className="text-[10px]" style={{
                            background: row.estado?.toLowerCase() === 'cerrado' ? 'rgba(0,200,83,0.12)' : 'rgba(255,107,53,0.12)',
                            color: row.estado?.toLowerCase() === 'cerrado' ? '#00C853' : '#FF6B35',
                            border: `1px solid ${row.estado?.toLowerCase() === 'cerrado' ? 'rgba(0,200,83,0.3)' : 'rgba(255,107,53,0.3)'}`,
                          }}>{row.estado || '-'}</Badge>
                        </TableCell>
                        <TableCell className="pr-2 w-8 text-right">
                          {isAdmin && (row.objectid || row.OBJECTID) && (
                            <button
                              onClick={(e) => deleteRecord(row.objectid || row.OBJECTID, e)}
                              className="opacity-30 hover:opacity-100 transition-opacity"
                              title="Eliminar Near Miss"
                              data-testid="nm-delete-btn"
                            >
                              <Trash2 size={13} className="text-red-400" />
                            </button>
                          )}
                        </TableCell>
                      </TableRow>
                      {expandedRow === i && (
                        <TableRow data-testid="nm-expanded-row" style={{ borderColor: 'var(--vz-border-subtle)' }}>
                          <TableCell colSpan={12} className="p-0">
                            <ExpandedDetailNM row={row} />
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))
            }
          </TableBody>
        </Table>
      </ScrollArea>
    </div>
  );
}

const ROL_LABELS_SHORT = {
  gerente: 'Gerente', jefe: 'Jefe de centro', mando_intermedio: 'Mando intermedio', otro: 'Otro',
};

function ResumenResponsablesTable({ filterParams, activeResponsable, onFilter }) {
  const { token } = useUserStore();
  const { data, isLoading } = useQuery({
    queryKey: ['nm-resumen-resp', filterParams],
    queryFn: () => fetchNMResumenResponsables(filterParams),
    enabled: !!token,
    staleTime: 60000,
  });
  const rows = data?.rows ?? [];
  const trackingEstado = data?.tracking_estado ?? false;

  if (!isLoading && !rows.length) return null;

  return (
    <div className="rounded-2xl border overflow-hidden" data-testid="nm-resumen-responsables" style={{ background: 'var(--vz-bg-card)', borderColor: 'var(--vz-border-subtle)' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--vz-border-subtle)' }}>
        <div className="flex items-center gap-2">
          <UserCheck size={14} style={{ color: '#FF9800' }} />
          <span className="font-montserrat text-xs font-bold uppercase tracking-[0.14em]" style={{ color: 'var(--vz-text-secondary)' }}>
            Seguimiento por Responsable
          </span>
        </div>
        <span className="text-[10px]" style={{ color: 'var(--vz-text-muted)' }}>
          {activeResponsable
            ? <button onClick={() => onFilter(null)} className="underline underline-offset-2 hover:opacity-70 transition-opacity">Quitar filtro: {activeResponsable}</button>
            : trackingEstado ? 'Haz clic en una fila para filtrar los registros' : 'Sin seguimiento de estado en origen (ArcGIS)'}
        </span>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-20">
          <div className="w-5 h-5 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#FF9800' }} />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b" style={{ borderColor: 'var(--vz-border-subtle)', background: 'var(--vz-bg-elevated)' }}>
                <th className="text-left px-4 py-2 text-[9px] uppercase tracking-wider font-bold" style={{ color: 'var(--vz-text-muted)' }}>Responsable</th>
                <th className="text-left px-3 py-2 text-[9px] uppercase tracking-wider font-bold hidden sm:table-cell" style={{ color: 'var(--vz-text-muted)' }}>Rol</th>
                <th className="text-center px-3 py-2 text-[9px] uppercase tracking-wider font-bold" style={{ color: 'var(--vz-text-muted)' }}>Total</th>
                {trackingEstado && <>
                  <th className="text-center px-3 py-2 text-[9px] uppercase tracking-wider font-bold" style={{ color: '#FF6B35' }}>Pendientes</th>
                  <th className="text-center px-3 py-2 text-[9px] uppercase tracking-wider font-bold hidden md:table-cell" style={{ color: '#00A847' }}>Cerrados</th>
                  <th className="text-left px-4 py-2 text-[9px] uppercase tracking-wider font-bold hidden lg:table-cell" style={{ color: 'var(--vz-text-muted)' }}>Progreso cierre</th>
                </>}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const isActive = activeResponsable === row.responsable_nombre;
                const hasPending = row.pendientes > 0;
                return (
                  <tr
                    key={row.responsable_nombre}
                    onClick={() => onFilter(isActive ? null : row.responsable_nombre)}
                    data-testid={`resp-row-${i}`}
                    className="border-b cursor-pointer transition-colors duration-150"
                    style={{
                      borderColor: 'var(--vz-border-subtle)',
                      background: isActive ? 'rgba(255,152,0,0.08)' : 'transparent',
                    }}
                    onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'var(--vz-bg-elevated)'; }}
                    onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        {isActive && <div className="w-1 h-4 rounded-full bg-orange-500" />}
                        <span className="font-semibold uppercase text-[10px]" style={{ color: 'var(--vz-text-primary)' }}>
                          {row.responsable_nombre}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 hidden sm:table-cell" style={{ color: 'var(--vz-text-muted)' }}>
                      {ROL_LABELS_SHORT[row.responsable_rol] || (row.responsable_rol || '—').replace(/_/g, ' ')}
                    </td>
                    <td className="px-3 py-2.5 text-center font-bold" style={{ color: 'var(--vz-text-secondary)' }}>
                      {row.total}
                    </td>
                    {trackingEstado && <>
                      <td className="px-3 py-2.5 text-center">
                        {hasPending ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold"
                            style={{ background: 'rgba(255,107,53,0.12)', color: '#FF6B35', border: '1px solid rgba(255,107,53,0.3)' }}>
                            <Clock size={9} />
                            {row.pendientes}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
                            style={{ background: 'rgba(0,168,71,0.1)', color: '#00A847', border: '1px solid rgba(0,168,71,0.25)' }}>
                            0
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-center hidden md:table-cell font-semibold" style={{ color: '#00A847' }}>
                        {row.cerrados}
                      </td>
                      <td className="px-4 py-2.5 hidden lg:table-cell min-w-[140px]">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--vz-border-subtle)' }}>
                            <div
                              className="h-full rounded-full transition-all duration-500"
                              style={{
                                width: `${row.pct_cierre || 0}%`,
                                background: row.pct_cierre >= 80 ? '#00A847' : row.pct_cierre >= 40 ? '#D4A300' : '#FF6B35',
                              }}
                            />
                          </div>
                          <span className="text-[10px] font-bold w-8 text-right" style={{ color: 'var(--vz-text-muted)' }}>
                            {row.pct_cierre || 0}%
                          </span>
                        </div>
                      </td>
                    </>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function NearMisses() {
  const globalFilters = useFilterParams();
  const { token } = useUserStore();
  const [moduleFilters, setModuleFilters] = useState({});
  const tableRef = React.useRef(null);
  const baseFilters   = useMemo(() => ({ desde: globalFilters.desde, hasta: globalFilters.hasta }), [globalFilters.desde, globalFilters.hasta]);
  const mergedFilters = useMemo(() => ({ ...baseFilters, ...moduleFilters }), [baseFilters, moduleFilters]);

  const handleFilterChange = (key, value) => setModuleFilters(prev => ({ ...prev, [key]: value }));
  const handleReset = () => setModuleFilters({});

  const handleFilterByResponsable = (nombre) => {
    setModuleFilters(prev => ({ ...prev, responsable: nombre || undefined }));
    if (nombre && tableRef.current) {
      setTimeout(() => tableRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }
  };

  const { supported: pushSupported, subscribed, loading: pushLoading, subscribe, unsubscribe } = usePushNotifications();

  const enabled = !!token;
  const { data: porCategoria,  isLoading: catLoading      } = useQuery({ queryKey: ['nm-categoria', mergedFilters], queryFn: () => fetchNMPorCategoria(mergedFilters), enabled });
  const { data: timeline,      isLoading: timelineLoading  } = useQuery({ queryKey: ['nm-timeline',  mergedFilters], queryFn: () => fetchNMTimeline(mergedFilters), enabled });
  const { data: porGravedad,   isLoading: gravLoading      } = useQuery({ queryKey: ['nm-gravedad',  mergedFilters], queryFn: () => fetchNMPorGravedad(mergedFilters), enabled });
  const { data: resumen,       isLoading: resumenLoading   } = useQuery({ queryKey: ['nm-resumen',   mergedFilters], queryFn: () => fetchNMResumen(mergedFilters), staleTime: 60000, enabled });

  return (
    <div data-testid="near-misses-page">
      <GlobalFilterBar />
      <div className="px-4 lg:px-6 py-6 space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-montserrat font-bold text-lg" style={{ color: 'var(--vz-text-primary)' }}>Near Misses</h2>
            <p className="text-xs mt-0.5" style={{ color: 'var(--vz-text-muted)' }}>Cuasi-accidentes registrados</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {pushSupported && (
              <button
                onClick={subscribed ? unsubscribe : subscribe}
                disabled={pushLoading}
                data-testid="push-toggle-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all duration-200"
                style={{
                  background: subscribed ? 'rgba(0,200,83,0.1)' : 'rgba(74,158,255,0.1)',
                  color: subscribed ? 'var(--vz-green)' : 'var(--vz-blue)',
                  border: `1px solid ${subscribed ? 'rgba(0,200,83,0.25)' : 'rgba(74,158,255,0.25)'}`,
                }}
                title={subscribed ? 'Desactivar notificaciones push' : 'Activar notificaciones push del navegador'}
              >
                {subscribed ? <BellOff size={12} /> : <Bell size={12} />}
                {pushLoading ? 'Procesando...' : subscribed ? 'Notificaciones activas' : 'Activar alertas'}
              </button>
            )}
            <ExportButtons tipo="near_misses" filters={mergedFilters} />
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="nm-kpi-row">
          <KpiGlowCard
            title="Total Near Misses"
            value={resumen?.total}
            color="#FF6B35"
            icon={AlertTriangle}
            loading={resumenLoading}
            delta={resumen?.delta_pct}
            sub={resumen?.delta_pct != null ? `vs mismo período ${new Date().getFullYear() - 1}` : 'Registros con filtro activo'}
          />
          <KpiGlowCard title="HIPO (Gravedad Roja)"    value={resumen?.hipo ?? '—'} sub="Alto potencial lesivo" color="#FF1744" icon={Flame} loading={resumenLoading} />
          <KpiGlowCard title="% Gravedad Roja"         value={resumen?.pct_roja ?? 0} unit="%" sub="Sobre total de NMs" color="#FF5252" icon={Flame} loading={resumenLoading} />
          <KpiGlowCard title="% Gravedad Amarilla"     value={resumen?.pct_amarilla ?? 0} unit="%" sub="Sobre total de NMs" color="#FFD600" icon={AlertTriangle} loading={resumenLoading} />
          <KpiGlowCard title="Centros que identifican" value={resumen?.centros ?? '—'} sub="Centros de trabajo distintos" color="#4A9EFF" icon={Building2} loading={resumenLoading} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <ChartCard title="Evolución mensual" subtitle="Near Misses por mes" loading={timelineLoading} isEmpty={!timeline?.length} className="lg:col-span-2">
            <TrendLineChart data={timeline || []} color="#FF6B35" label="Near Misses" height={220} />
          </ChartCard>

          <ChartCard title="Por gravedad" loading={gravLoading} isEmpty={!porGravedad?.length}>
            <DonutChart
              data={(() => {
                // Order: green → yellow → orange → red so the legend reads from
                // safe to severe, regardless of how the API returns the rows.
                const order = ['verde', 'amarillo', 'naranja', 'rojo'];
                const indexOf = (g) => {
                  const idx = order.indexOf((g || '').toLowerCase());
                  return idx === -1 ? 99 : idx;
                };
                return [...(porGravedad || [])]
                  .sort((a, b) => indexOf(a.gravedad) - indexOf(b.gravedad))
                  .map((g) => ({ name: g.gravedad, total: g.total }));
              })()}
              nameKey="name" dataKey="total" height={220}
              colorMap={GRAVEDAD_COLORS}
            />
          </ChartCard>
        </div>

        <ChartCard title="Categorías de Near Misses" loading={catLoading} isEmpty={!porCategoria?.length}>
          <HorizontalBarChart
            data={(porCategoria || []).map((c) => ({ name: c.categoria, total: c.total }))}
            nameKey="name" dataKey="total" color="#FF6B35"
            height={Math.max(180, (porCategoria?.length || 1) * 32)}
          />
        </ChartCard>

        {/* Responsable summary table */}
        <ResumenResponsablesTable
          filterParams={mergedFilters}
          activeResponsable={moduleFilters.responsable || null}
          onFilter={handleFilterByResponsable}
        />

        <div ref={tableRef} className="rounded-2xl border p-4 space-y-4" style={{ background: 'var(--vz-bg-card)', borderColor: 'var(--vz-border-subtle)' }}>
          <h3 className="font-montserrat text-xs font-bold uppercase tracking-[0.16em]" style={{ color: 'var(--vz-text-secondary)' }}>Registros Near Misses</h3>
          <ModuleFilterBar filters={moduleFilters} onChange={handleFilterChange} onReset={handleReset} filterDefs={NM_FILTER_DEFS} />
          <NMTable filters={moduleFilters} />
        </div>
      </div>
    </div>
  );
}
